import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi, setLoggingIn } from '@task-bazaar/api'
import { generateFingerprint } from '../utils/fingerprint'

export interface UserInfo {
  id: string
  username: string
  nickname: string
  avatar: string
  phone?: string
  role?: string
  balance: number
  totalPublished?: number
  totalCompleted?: number
  inviteCode: string
  membershipType?: string
  membershipExpireAt?: string | null
  createdAt: string
}

// 检查会员是否有效
export function isMembershipValid(user: UserInfo | null): boolean {
  if (!user || !user.membershipType || user.membershipType === 'none') {
    return false
  }
  if (!user.membershipExpireAt) {
    return false
  }
  return new Date(user.membershipExpireAt) > new Date()
}

// 获取会员类型名称
export function getMembershipName(type?: string): string {
  switch (type) {
    case 'monthly': return '月卡会员'
    case 'quarterly': return '季卡会员'
    case 'yearly': return '年卡会员'
    default: return '普通用户'
  }
}

interface AuthState {
  token: string | null
  refreshToken: string | null
  user: UserInfo | null
  isLoggedIn: boolean
  isHydrated: boolean

  // Actions
  login: (username: string, password: string) => Promise<void>
  register: (username: string, password: string, inviteCode?: string, captchaToken?: string, captchaAnswer?: string) => Promise<void>
  logout: () => void
  loadProfile: () => Promise<void>
  updateUser: (data: Partial<UserInfo>) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      isLoggedIn: false,
      isHydrated: false,

      login: async (username: string, password: string) => {
        console.log('auth store login 开始，设置 isLoggingIn = true')
        setLoggingIn(true)
        try {
          const response = await authApi.login({ username, password })
          const result = response.data
          set({
            token: result.accessToken,
            refreshToken: result.refreshToken,
            user: result.user,
            isLoggedIn: true,
          })
          // 登录后跳转
          setTimeout(() => {
            if (result.user?.role === 'admin') {
              window.location.href = '/admin'
            } else {
              window.location.href = '/'
            }
          }, 100)
        } catch (error: any) {
          console.log('auth store login 捕获到错误:', error.message)
          // 登录失败，直接抛出错误，不触发退出登录
          throw error
        } finally {
          console.log('auth store login finally，设置 isLoggingIn = false')
          setLoggingIn(false)
        }
      },

      register: async (username: string, password: string, inviteCode?: string, captchaToken?: string, captchaAnswer?: string) => {
        const fingerprint = await generateFingerprint()
        const response = await authApi.register({
          username,
          password,
          inviteCode,
          captchaToken: captchaToken || '',
          captchaAnswer: captchaAnswer || '',
          deviceFingerprint: fingerprint,
        })
        const result = response.data
        set({
          token: result.accessToken,
          refreshToken: result.refreshToken,
          user: result.user,
          isLoggedIn: true,
        })
      },

      logout: () => {
        console.log('logout 方法被调用')
        set({
          token: null,
          refreshToken: null,
          user: null,
          isLoggedIn: false,
        })
        localStorage.removeItem('auth-storage')
        // 使用 setTimeout 确保状态更新后再跳转
        setTimeout(() => {
          console.log('logout 跳转到首页')
          window.location.href = '/'
        }, 100)
      },

      loadProfile: async () => {
        try {
          const response = await authApi.getProfile()
          const profile = response.data
          set({ user: { ...get().user, ...profile } as UserInfo })
        } catch (error) {
          console.error('Failed to load profile:', error)
        }
      },

      updateUser: (data: Partial<UserInfo>) => {
        const currentUser = get().user
        if (currentUser) {
          set({ user: { ...currentUser, ...data } })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isLoggedIn: state.isLoggedIn,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isHydrated = true
        }
      },
    },
  ),
)

// 监听退出登录事件（token 过期时触发）
if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    console.log('收到 auth:logout 事件，执行 logout')
    useAuthStore.getState().logout()
  })
}
